import { Translation } from "./index";
import { RoomStates } from "@store/space/types";
import { NavLabels, SubNavLabels } from "@components/Layout";
import { IssueState } from "@store/issue/types";
import { UserRoles } from "@store/user/types";
import { Frequencies } from "@store/recurringIssues/types";

const en: Translation = {
  common: {
    all: "All",
    notApplicable: "N/A",
    cancel: "Cancel",
    delete: "Delete",
    submit: "Submit",
    back: "Go back",
    filter: "Filter",
    logout: "Logout",
    loading: "Loading...",
    space: "Room",
    spaces: "Rooms",
    issue: "Task",
    issues: "Tasks",
    photos: "Photos",
    area: "Area",
    areas: "Areas",
    property: "Property",
    roomType: "Room type",
    customSpace: "Custom room",
    transactions: "Data",
    room: "Room",
    action: "Action",
    date: "Date",
    export: "Export {{value}}",
    save: "Save",
    total: "Total",
    change: "Change",
    name: "Name",
    giveName: "Enter a name",
    floor: "Floor",
    youSure: "Are you sure?",
    unknown: "Unknown",
    noData: "No data available",
    add: "Add",
    tryAgain: "Try again",
    remark: "Remark",
    resetFilter: "Reset filter",
    general: "General",
    rooms: "Rooms",
    optional: "Optional",
    view: "View",
    priority: "Priority",
    tasks: "Periodic tasks",
    saveChanges: "Save changes",
    and: "and",
    selectDate: "Select date",
    feedback: "Feedback",
  },
  layout: {
    refresh: "Refresh",
    refreshMessage: 'Press "Refresh" for the most up-to-date data.',
    fold: "Fold",
    menu: {
      [NavLabels.Dashboard]: "Dashboard",
      [NavLabels.Overview]: "Rooms",
      [NavLabels.AppLogin]: "Log in",
      [NavLabels.DayReports]: "Week overview",
      [NavLabels.TdOverview]: "Technical Service",
      [NavLabels.GsOverview]: "General Service",
      [NavLabels.TeamSettings]: "Manage team",
      [NavLabels.AreaSettings]: "Manage sections",
      [NavLabels.CustomRoomSettings]: "Manage rooms",
      [NavLabels.RecurringIssues]: "Manage tasks",
      [NavLabels.DayReportSettings]: "Manage day closure",
      [NavLabels.ActionsLog]: "Room Actions",
      [SubNavLabels.Settings]: "Settings",
      [SubNavLabels.None]: "None",
      [NavLabels.DayOverview]: "Day report",
      [NavLabels.ExtraTasks]: "Extra tasks",
      [NavLabels.TypeGroups]: "Rate type groups",
    },
  },
  dayreport: {
    endOfDay: "End of day",
    weekOverview: "Week overview",
    expectedOccupancy: "Expected occupancy",
    extra: "Extra tasks",
    deletedExtra:
      "This extra has been deleted. You can still view the value but you can no longer change it.",
    addRemark: "Add a remark",
    writeRemark: "Write your remark here",
    noRemarks: "No remarks have been added yet.",
    noChanges: "No changes have been made yet.",
    dayView: "Day view",
    saveAndApprove: "Save and approve",
    approve: "Approve",
    approval: "Approval",
    unsavedChanges: "You have unsaved changes. Are you sure you want to leave?",
    difference: "Difference",
    sold: {
      title: "Sold",
      description:
        "the number of rooms sold for that night, including no-shows. The no-shows are subtracted from this to get to 'To clean'. The figures update after a change in the PMS, as long as the day report has not been approved.",
    },
    carriedOverFromYesterday: {
      title: "Carried over from yesterday",
      description: "all rooms from yesterday that have not been cleaned yet.",
    },
    dayuse: {
      title: "Day use",
      description:
        "all rooms that we receive from the PMS as day use. The figures update after a change in the PMS, as long as the day report has not been approved.",
    },
    carriedOver: {
      title: "Carried over to today",
      description:
        "rooms that are still dirty but left yesterday (and not today).",
    },
    totalDescription:
      "the sold rooms, day use and the rooms carried over from yesterday added together.",
    green: {
      title: "Green Choice",
      description:
        "rooms where the guest (via email) or reception (via ServiceV4) has indicated that no cleaning is required (green choice).",
    },
    orange: {
      title: "Orange Choice",
      description:
        "rooms where the guest (via email) or reception (via ServiceV4) has indicated that only towels or a bag (orange choice) are required + the rooms that have been set to 'bag hung' via the HKTD app.",
    },
    nonGreenSkip: {
      title: "Cleaning skipped",
      description:
        "rooms where the guest without green choice has chosen to skip the cleaning.",
    },
    doNotDisturb: {
      title: "Do not disturb",
      description:
        "the rooms that have been set to 'do not disturb' via the HKTD app.",
    },
    cleaned: {
      title: "To clean",
      description:
        "the sold rooms, day use and the rooms carried over from yesterday added together, minus the no-shows and the rooms carried over to today. The exceptions are then removed: green choice, orange choice, cleaning skipped and do not disturb.",
    },
    totalCleaned: {
      title: "Total cleaned",
      description:
        "these are the rooms that have been set to clean via the HKTD app.",
    },
    note: {
      title: "Note",
      description:
        "certain numbers can be adjusted manually by the HKS or FOM, the adjusted numbers will then be included in the calculation.",
    },
    calculate: "How is this calculated",
    noData: "No data available. This data will be retrieved at a later time.",
    noShow: "No-show",
    clean: "Cleaned",
    oldValue: "Old value",
    new_Value: "New value",
    whoMay: {
      send: "Who may send the day report",
      edit: "Who may edit the day report",
      create: "Who may create extra tasks",
      receive: "Who may receive the day report",
    },
    setupEmail: "Setup email addresses",
    rateTypeGroup: "Change rate type group",
    editRateType: "View or edit rate type",
    week: "Week",
    editWarning: "Manual changes have been applied",
    send: {
      title: "Are you sure you want to send the weekly review?",
      description: "This weekly review is sent to:",
      button: "Yes, send this week",
      self: "Send",
      export: "Export overview",
    },
    manage: "Managing the day report",
    popover: {
      green: "Green choice <strong>{{number}}</strong>",
      orange: "Orange choice <strong>{{number}}</strong>",
      nonGreenSkip: "Skipped cleaning <strong>{{number}}</strong>",
      doNotDisturb: "Do not disturb <strong>{{number}}</strong>",
    },
    exceptions: "Exceptions",
    enableEmail:
      "To take full advantage of the week overview (and the day closure), this module must be turned on.",
    noEmail:
      "No email address has been added, the week overview cannot be sent.",
    settings: {
      reportPermission: {
        fom: "Front office",
        fomHk: "Front office & Housekeeping Supervisor",
        hk: "Housekeeping Supervisor",
        gs: "General service Supervisor",
        hkGs: "Housekeeping & General service Supervisor",
        fomGs: "Front office & General service",
        fomHkGs: "Front office, Housekeeping & General service Supervisor",
      },
    },
  },
  hkStatistics: {
    tableContent: {
      arrivals: "Arrivals",
      atStart: "Start",
      carriedOver: "Carried over",
      carriedOverFromYesterday: "Carried over from yesterday",
      currently: "Currently",
      departures: "Departures",
      dirtyVacant: "All vacant/dirty rooms",
      notAvailable: "Statistics are not (yet) available",
      roomsCleaned: "Rooms cleaned",
      stayovers: "Stayovers",
      rateTypesCleaned: "Suites cleaned",
    },
    emptyMessage: "The statistics could not be retrieved.",
    atStart: "Start",
    currently: "Currently",
  },
  forecast: {
    occupiedLeft: "Occupied (left)",
    availableRight: "Available (right)",
    expectedArrivals: "Expected arrivals all room types",
    expectedDepartures: "Expected departures",
    expectedStayovers: "Expected stayers",
    totalToClean: "Total to clean (stayers + departures)",
    totalRoomsLeft: "Total rooms (left)",
    totalGuestsRight: "Total guests (right)",
    rooms: "Other rooms total",
    suites: "Luxury rooms total",
    lastSync: "Last sync",
  },
  pmsNotification: {
    content: {
      bad: "The room statuses are not up-to-date due to a connection problem with PMS.",
      poor: "The room statuses may not be up-to-date due to a connection problem with PMS.",
    },
    heading: {
      bad: "Unable to connect to PMS for a longer time",
      poor: "Unable to connect to PMS",
    },
  },
  actions: {
    export: {
      failed: "Exporting {{value}} failed.",
    },
    assign: {
      failed: "{{value}} is not assigned.",
      success: "{{value}} assigned successfully.",
    },
    unassign: {
      failed: "Unassigning {{value}} failed.",
      success: "Unassigning {{value}} successful.",
      success_plural: "Unassigning {{value}} successful.",
    },
    cleaning: {
      start: {
        failed: "Starting cleaning failed",
        success: "Starting cleaning successful",
      },
      stop: {
        failed: "Stopping cleaning failed",
        success: "Stopping cleaning successful",
      },
      finish: {
        failed: "Cannot set room to clean",
        success: "Room set to clean successfully",
      },
    },
    inspectionFinish: {
      failed: "Inspecting room failed",
      success: "Room inspection successful",
    },
    issueUpdate: {
      failed: "Task status update failed",
      success: "Task status update successful",
    },
    add: {
      failed: "Adding {{value}} failed.",
      success: "Adding {{value}} successful.",
      success_plural: "Adding {{value}} successful.",
      userFailed:
        "Note: it is not possible to use the same email address for multiple users/hotels.",
    },
    default: {
      failed: "Failed.",
      success: "{{value}} update successful",
    },
    update: {
      failed: "Updating {{value}} failed.",
      success: "{{value}} update successful",
      success_plural: "{{value}} update successful",
    },
    delete: {
      failed: "Deleting {{value}} failed.",
      success: "{{value}} delete successful",
      success_plural: "{{value}} delete successful",
    },
    approved: {
      failed: "Approving {{value}} failed.",
      success: "{{value}} approved successfully",
    },
    send: {
      failed: "Sending {{value}} failed.",
      success: "{{value}} send successful",
    },
    refresh: {
      failed: "Refreshing {{value}} failed.",
      success: "{{value}} refreshed successfully",
    },
    switch: {
      failed: "Switching {{value}} failed.",
      languageFailed: "Could not switch notification language.",
      success: "{{value}} switched.",
    },
    qrCode: {
      failed: "Exporting the QR codes failed.",
      success: "The QR codes have been successfully downloaded.",
    },
    fetch: {
      failed:
        "The information cannot be retrieved at the moment.\n  Check your internet and try again.\n\n If the problem persists, try logging in again.",
    },
  },
  hk: {
    insights: "Insights",
    description: "Description: {{description}}",
    leavers: "Leavers",
    stayers: "Stayers",
    roomState: {
      [RoomStates.OUT_OF_INVENTORY]: "Out of inventory",
      [RoomStates.OUT_OF_ORDER]: "Out of order",
      [RoomStates.OUT_OF_SERVICE]: "Out of service",
      cleanTowels: "towels hung",
      dirtyTowels: "hang towels",
      dirtyAndInspected: "dirty and inspected",
      cleanAndInspected: "clean and inspected",
      clean: "clean",
      dirty: "dirty",
      extra: {
        vacantToday: "vacant since today",
        leaving: "leaving",
        occupiedToday: "occupied since today",
        staying: "occupied | {{current}}/{{total}}",
        vacant_zero: "vacant since today",
        vacant_one: "vacant | {{count}} day",
        vacant_other: "vacant | {{count}} days",
        dirtySince_one: "{{count}} day",
        dirtySince_other: "{{count}} days",
        orange: "orange choice",
        skipCleaning: "skip cleaning (green choice)",
        doNotDisturb: "do not disturb",
      },
    },
    amountExtra: "{{amount}} extra tasks",
    filters: {
      skipCleaning: "Skip cleaning (green choice)",
      roomState: "Room state (+ extras)",
      guests: "Guests",
      arrivals: "Arrivals (+ extras)",
      cleaningState: "Cleaning state",
      tasks: "Tasks",
      user: "User",
      roomsWithExtra: "Rooms with extra tasks",
      roomsWithTasks: "Rooms with tasks",
      occupiedToday: "Occupied since today",
      rooms: {
        dirty: "To clean today",
        clean: "Clean rooms",
      },
      legionella: "Legionella",
      dirty2Days: "Dirty 2+ days",
    },
    selectedRooms: "Selected rooms: {{count}}",
    assignRoom: "Assign room",
    showQrCode: "Hover your mouse over this area for the QR code",
    loading: "Processing task and room statuses...",
  },
  user: {
    sessionExpired: "Your session has expired. Please log in again.",
    self: "User",
    role: "Role",
    switch: 'Switch "{{value}}"',
  },
  tasks: {
    delete: {
      title: "Delete {{value}}",
      description:
        "Are you sure you want to delete {{value}}? Deletion cannot be undone. Deletion will take effect from the next working day.",
      result: "The task '{{label}}' has been deleted.",
    },
    number: "Task {{task}}",
    add: "Add task",
    assign: "Assign",
    assignTask: "Assign task",
    assignTasks: "Assign tasks",
    unassignTasks: "Unassign tasks",
    unassignUser: "Unassign user",
    all: "+ Add an extra task",
    empty: "No periodic tasks created yet.",
    labelPlaceholder: "Cleaning",
    remove: "Remove task",
    create: {
      result: "The task '{{label}}' has been created.",
      success: "The task '{{label}}' has been created.",
      failed: "Failed to create the task '{{label}}'.",
    },
    update: {
      result: "The task '{{label}}' has been updated.",
      success: "The task has been updated.",
      failed: "Failed to update task.",
    },
    months: "Month",
    type: "Task type",
    housekeeping: "Housekeeping",
    technical: "Technical",
    general: "General",
    addNew: "Create new periodic task",
    change: "Edit periodic task",
    responsibility: "Responsibility",
    startDate: "Start date",
    endDate: "End date",
    onNextOccurrence: "On Next Occurrence",
    onFrequency: "Frequency",
    onInterval: "Interval",
    setEndDate: "Set end date",
    pastDate: "Past Date",
    nextOccurrence: "Next occurrence",
    name: "Task name",
    locations: "Location(s)",
    frequency: "Frequency",
    actions: "Actions",
  },
  actionLog: {
    actions: {
      space: {
        clean: {
          start: "cleaning started",
          stop: "cleaning stopped",
          finish: "cleaning finished",
          skip: "cleaning skipped",
        },
        assign: {
          add: "cleaner assigned",
          remove: "cleaner unassigned",
        },
        inspect: {
          finish: "inspection finished",
          approved: "inspection finished & approved",
          rejected: "inspection finished & rejected",
        },
      },
      issue: {
        work: {
          start: "task started",
          stop: "task stopped",
          finish: "task resolved",
          wait: "task in order",
        },
        report: {
          create: "new task reported",
          attach: "attachment added",
        },
        assign: {
          add: "task assigned",
          remove: "task unassigned",
        },
      },
    },
  },
  search: {
    title: "Search",
    rooms: "Search a room",
    noResults: "No {{object}} found for this search query",
    name: "Search by name",
    employee: "Search an employee",
    notFound:
      "Unfortunately, no {{search}} found for the current search query and/or filters.",
    role: "Select a role",
    task: "Search a task",
    roomOrHouseKeeper: "Search a room or housekeeper",
    location: "Search a location",
    taskPersonRoom: "Search a task, person or room",
    rateType: "Search a rate type",
    function: "Select a function",
    taskRoom: "Search for task or room",
  },
  area: {
    name: "Section name",
    numberOfRooms: "Number of rooms",
    assignedRooms: "Assigned rooms",
    noRoomsAssigned: "No rooms assigned yet",
    emptyMessage:
      "No sections found. Please create a section to assign rooms to.",
    delete: {
      action: "Remove from section",
      description:
        "You are about to remove assigned rooms from a section. \nThis action cannot be undone.",
      area: "You are about to remove area '{{count}}'. \nThis action cannot be undone.",
    },
    assignSection_zero: "Assign to section",
    assignSection: "Assign to section ({{count}})",
    select: "Select a section",
    count: "Number of rooms: {{count}}",
    toAssign: "Assign a section",
    remove: "Remove section",
    update: "Update section",
    add: "Add section",
    charLeft_one: "{{count}} character left",
    charLeft_other: "{{count}} characters left",
    charLeft_zero: "No characters left",
  },
  counts: {
    room_one: "{{count}} Room",
    room_other: "{{count}} Rooms",
  },
  customroom: {
    create: {
      title: "Create public space",
      submit: "Create room",
      add: "Add room",
      nameRequired: "The public space must have a name",
      nameInUse: "This name is already in use.",
      groupRequired: "Groupname is required",
    },
    changeTitle: "Change room",
    floor: "Floor {{floor}}",
    floorPlaceholder: "E.g. 0 for the ground floor, -1 for the basement",
    floorInvalid: "The floor must be a whole number, for example 2, 0 or -1.",
    tooltip: "Tip: a short title works best.",
  },
  extra: {
    description: "description of extra",
    hourAndTask: "Extra tasks and hours",
    newTask: "New task",
    typeName: "Enter a name",
    noExtra: "No extra tasks have been added yet.",
    newTaskAction: "Click to view or edit extras",
    self: "Extras",
  },
  login: {
    alreadyadded: "The email address {{email}} has already been added.",
    invalid: "The email address {{email}} is not valid.",
    enterNew: "Enter a new email address",
    self: "E-mail address",
    password: "Password",
    loginError: "Incorrect email address or password",
    forgotPassword: "Forgot password?",
  },
  appLogin: {
    qrCodeInfo:
      "<strong>Log in with a QR code</strong> \nHousekeeping can log in with a QR code. Other roles (such as TS or front office) use the HKTD app with a username and password. \nIf you have any questions, contact <0>hotelsupport@valkdigital.nl</0>",
  },
  issue: {
    typeTaskName: "Enter a task name",
    typeReporterName: "Enter a reporter name",
    createdBy: "Created by",
    reportedBy: "Reported by",
    createdAt: "Created at",
    addRemark: "Add a remark to this task",
    priority: "Does this task have priority?",
    change: "Change task",
    create: "Create task",
    addNewTask: "Create new task",
    newTask: "New task",
    states: {
      title: "Statuses",
      [IssueState.OPEN]: "Open",
      [IssueState.IN_PROGRESS]: "In progress",
      [IssueState.ON_HOLD]: "On hold",
      [IssueState.ORDERED]: "Ordered",
      [IssueState.DONE]: "Done",
      all: "All",
    },
    createDate: "Create date",
    completedDate: "Completed date",
    taskName: "Task name",
    status: "Status",
    assignee: "Assignee",
    changeLog: "Change log",
    changeDetails: "Change details",
    delete: {
      title: "Delete task",
      action: "Delete task",
      description:
        "You are about to delete task {{label}}. \nThis action cannot be undone.",
    },
    inactive: {
      title: "Set task to inactive",
      action: "Why do you want to set this task to inactive?",
      placeholder: "Add a reason",
      label: "Reason",
    },
    edit: "Change task",
    view: "View task details",
    addImage: "Add image",
    addAttachment: "Add attachments",
    videoAttachment: "Video attachment",
    noPreviewAvailable: "No preview available",
    noAttachment: "No attachment yet",
    export: "Export tasks",
    newIssueModal: {
      createdIssuesTitle: "Other task for this space",
      columns: {
        date: "Date",
        task: "Task",
        status: "Status",
      },
    },
  },
  rateType: {
    create: "New ratetype",
    update: "Update ratetype",
    delete: "Delete ratetype",
    groupName: "Groupname",
    groupDescription: "Enter a groupname",
    defaultGroup:
      "You cannot delete the default group. You may assign the room types to another group.",
    roomType: "Room type",
    belongsTo: "Belongs to",
    deleteDescription:
      "The deleted room types will be restored under the group 'Rooms' or 'Suites'",
    deleteConfirmation:
      "You are about to delete rate type {{name}}. \nThis action cannot be undone.",
    groups: "rateType groups",
    group: "Group",
    groupedRatesInfo: "Changes will only take effect from next Monday.",
    countTypes: "Amount of room types",
  },
  segment: {
    ["Groepen"]: "Groups",
    ["Alle kamertypes"]: "All room types",
    all: "All",
  },
  team: {
    addMember: "Add team member",
    detail: "Details",
    removeMember: "Remove team member",
    inputName: "Enter a name",
    inputEmail: "Enter an email address",
    inputPassword: "Your own password",
    inputPasswordInfo: "Enter your password to change the email address.",
    inputEmailInfo:
      "By entering an email address, the email will receive an invitation to create an account.",
    deleteConfirmation:
      "You are about to delete {{value}}. \nThis action cannot be undone.",
  },
  roleNames: {
    [UserRoles.ADMIN]: "Admin",
    [UserRoles.HOTEL_MANAGER]: "Hotel manager",
    [UserRoles.HK_MANAGER]: "Housekeeping manager",
    [UserRoles.HK_MEMBER]: "Housekeeper",
    [UserRoles.TD_MANAGER]: "TS manager",
    [UserRoles.TD_MEMBER]: "TS employee",
    [UserRoles.TD_CREATOR]: "TS creator",
    [UserRoles.GS_MANAGER]: "GS manager",
    [UserRoles.GS_MEMBER]: "GS employee",
    [UserRoles.NONE]: "None",
  },
  months: {
    jan: "January",
    feb: "February",
    mar: "March",
    apr: "April",
    may: "May",
    jun: "June",
    jul: "July",
    aug: "August",
    sep: "September",
    oct: "October",
    nov: "November",
    dec: "December",
    all: "All",
  },
  frequencyTypes: {
    [Frequencies.Daily]: "Daily",
    [Frequencies.Weekly]: "Weekly",
    [Frequencies.Monthly]: "Monthly",
    [Frequencies.Yearly]: "Yearly",
  },
  frequencies: {
    daily_one: "Every day",
    daily_other: "Every {{count}} days",
    weekly_one: "Every week on {{date}}",
    weekly_other: "Every {{count}} weeks on {{date}}",
    monthly_one: "Every month on {{date}}",
    monthly_other: "Every {{count}} months on {{date}}",
    yearly_one: "Every year on {{date}}",
    yearly_other: "Every {{count}} years on {{date}}",
    manual: "Manual",
  },
  weekdays: {
    mon: "Monday",
    tue: "Tuesday",
    wed: "Wednesday",
    thu: "Thursday",
    fri: "Friday",
    sat: "Saturday",
    sun: "Sunday",
  },
};

export default en;
